import React, { useState, useEffect } from 'react';
import { FaBook, FaDownload, FaGlobe } from 'react-icons/fa';
import { FaBookBookmark } from 'react-icons/fa6';
import LoadingScreen from '../components/LoadingScreen';
import LoadingOverlay from '../components/LoadingOverlay';
import DateRangeFilter from '../components/DateRangeFilter';
import PublishedBooksChart from '../components/PublishedBooksChart';
import UserDownloadsChart from '../components/UserDownloadsChart';
import BooksPerLanguageChart from '../components/BooksPerLanguageChart';
import BooksPerLevelChart from '../components/BooksPerLevelChart';
import ThemeToggle from '../components/ThemeToggle';
import {
  fetchBookStatistics,
  fetchPublishedBooksPerDayByLanguage,
  fetchDownloadsPerDay,
  fetchBooksPerReadingLevel
} from '../services/api';
import { groupByWeek, groupByMonth, fillDateGaps, groupByWeekWithLanguages, groupByMonthWithLanguages, fillDateGapsWithLanguages } from '../utils/dataGrouping';
import { formatCompactNumber } from '../utils/formatters';
import './Pages.css';
import './DataPages.css';

const Home = ({ isDarkMode, toggleDarkMode }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Summary stats
  const [summaryStats, setSummaryStats] = useState({
    totalBooks: 0,
    totalDownloads: 0,
    periodPublished: 0,
    activeLanguages: 0
  });

  // Charts data
  const [publishedBooksPerDay, setPublishedBooksPerDay] = useState([]);
  const [userDownloadsPerDay, setUserDownloadsPerDay] = useState([]);
  const [booksPerLanguage, setBooksPerLanguage] = useState([]);
  const [booksPerLevel, setBooksPerLevel] = useState([]);

  // Raw data for view switching
  const [rawPublishedBooks, setRawPublishedBooks] = useState([]);
  const [rawDownloads, setRawDownloads] = useState([]);

  // Date range from API (for Max button)
  const [earliestDate, setEarliestDate] = useState(null);
  const [latestDate, setLatestDate] = useState(null);

  // Filters
  const [publishedViewType, setPublishedViewType] = useState('daily');
  const [downloadsViewType, setDownloadsViewType] = useState('daily');

  // Date range helper (UTC to match server time)
  const toUTCDateStr = (date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;

  const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setUTCDate(start.getUTCDate() - 29); // Default: last 30 days (including today)
    return {
      startDate: toUTCDateStr(start),
      endDate: toUTCDateStr(end)
    };
  };

  const [startDate, setStartDate] = useState(getDefaultDateRange().startDate);
  const [endDate, setEndDate] = useState(getDefaultDateRange().endDate);

  // Get days between dates
  const getDaysBetween = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Process data based on view type
  const groupData = (data, viewType) => {
    if (viewType === 'weekly') return groupByWeek(data);
    if (viewType === 'monthly') return groupByMonth(data);
    return data;
  };

  // Process language-aware data based on view type
  const groupDataWithLanguages = (data, viewType) => {
    if (viewType === 'weekly') return groupByWeekWithLanguages(data);
    if (viewType === 'monthly') return groupByMonthWithLanguages(data);
    return data;
  };

  // Load dashboard data
  const loadData = async (start, end, isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      // Fetch all data in parallel - all APIs now accept date filters
      const [stats, publishedBooksResult, downloadsByDayResult, booksByLevel] = await Promise.all([
        fetchBookStatistics(start, end),
        fetchPublishedBooksPerDayByLanguage(start, end),
        fetchDownloadsPerDay(start, end),
        fetchBooksPerReadingLevel(start, end)
      ]);

      // Extract items and date range from wrapped responses
      const publishedBooks = publishedBooksResult.items || [];
      const downloadsByDay = downloadsByDayResult.items || [];

      // Store date range for Max button
      if (publishedBooksResult.earliestDate) setEarliestDate(publishedBooksResult.earliestDate);
      if (publishedBooksResult.latestDate) setLatestDate(publishedBooksResult.latestDate);

      // Store raw data (fill date gaps so zero-count days are shown)
      const filledPublished = fillDateGapsWithLanguages(publishedBooks);
      const filledDownloads = fillDateGaps(downloadsByDay);
      setRawPublishedBooks(filledPublished);
      setRawDownloads(filledDownloads);

      // Process data based on current view
      setPublishedBooksPerDay(groupDataWithLanguages(filledPublished, publishedViewType));
      setUserDownloadsPerDay(groupData(filledDownloads, downloadsViewType));

      // Transform books by language data
      const languageData = Object.entries(stats.booksByLanguage || {}).map(([language, count]) => ({
        language,
        count
      }));
      setBooksPerLanguage(languageData);

      // Transform books by level data
      setBooksPerLevel(booksByLevel.map(item => ({
        level: item.level,
        count: item.count
      })));

      // Update summary stats
      setSummaryStats({
        totalBooks: stats.totalBooks || 0,
        totalDownloads: downloadsByDay.reduce((sum, item) => sum + item.count, 0),
        periodPublished: publishedBooks.reduce((sum, item) => sum + item.count, 0),
        activeLanguages: Object.keys(stats.booksByLanguage || {}).length
      });

      setLoading(false);
      setRefreshing(false);
      setTimeout(() => setIsLoaded(true), 100);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      setError(err.message || 'Failed to load dashboard data');
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Initial load
  useEffect(() => {
    const defaultRange = getDefaultDateRange();
    loadData(defaultRange.startDate, defaultRange.endDate, false);
    // eslint-disable-next-line
  }, []);

  // Reprocess data when view type changes
  useEffect(() => {
    if (rawPublishedBooks.length > 0 && rawDownloads.length > 0) {
      setPublishedBooksPerDay(groupDataWithLanguages(rawPublishedBooks, publishedViewType));
      setUserDownloadsPerDay(groupData(rawDownloads, downloadsViewType));
    }
  }, [publishedViewType, downloadsViewType, rawPublishedBooks, rawDownloads]);

  // Handle filter apply - accepts optional direct dates to avoid race condition
  const handleApplyFilters = (directStartDate = null, directEndDate = null) => {
    const fetchStartDate = directStartDate ?? startDate;
    const fetchEndDate = directEndDate ?? endDate;
    loadData(fetchStartDate, fetchEndDate, true);
  };

  // Get period label for header
  const getPeriodLabel = () => {
    if (!startDate || !endDate) return 'All Time';
    const days = getDaysBetween(startDate, endDate) + 1;

    if (days === 1) return 'Today';
    if (days === 7) return 'Last 7 Days';
    if (days === 30) return 'Last 30 Days';
    if (days === 90) return 'Last 90 Days';

    return `${days} Days`;
  };

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="error-state">
        <div className="error-message">
          <h2>Error Loading Dashboard</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`home-page page-container ${isLoaded ? 'loaded' : ''}`}>
      {/* Loading Overlay for filter refresh */}
      {refreshing && <LoadingOverlay />}

      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1>Dashboard</h1>
          <div className="page-header-badges">
            <div className="control-badge">
              <span className="badge-label">Period</span>
              <span className="badge-value">{getPeriodLabel()}</span>
            </div>
            <ThemeToggle isDarkMode={isDarkMode} onToggle={toggleDarkMode} />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="data-page-content">
        <div className="filters-container">
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onApply={handleApplyFilters}
            earliestDate={earliestDate}
            latestDate={latestDate}
            showMax={true}
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-container summary-container-4">
        <div className="summary-card card-1">
          <div className="card-icon">
            <FaBook size={24} />
          </div>
          <div className="card-content">
            <h3>Total Books</h3>
            <p className="card-value" title={summaryStats.totalBooks.toLocaleString()}>{formatCompactNumber(summaryStats.totalBooks)}</p>
          </div>
        </div>

        <div className="summary-card card-2">
          <div className="card-icon">
            <FaDownload size={24} />
          </div>
          <div className="card-content">
            <h3>Downloads</h3>
            <p className="card-value" title={summaryStats.totalDownloads.toLocaleString()}>{formatCompactNumber(summaryStats.totalDownloads)}</p>
          </div>
        </div>

        <div className="summary-card card-3">
          <div className="card-icon">
            <FaBookBookmark size={24} />
          </div>
          <div className="card-content">
            <h3>Published Books</h3>
            <p className="card-value" title={summaryStats.periodPublished.toLocaleString()}>{formatCompactNumber(summaryStats.periodPublished)}</p>
          </div>
        </div>

        <div className="summary-card card-4">
          <div className="card-icon">
            <FaGlobe size={24} />
          </div>
          <div className="card-content">
            <h3>Languages</h3>
            <p className="card-value">{summaryStats.activeLanguages}</p>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="charts-grid">
        <div className="chart-item chart-large">
          <PublishedBooksChart data={publishedBooksPerDay} viewType={publishedViewType} periodLabel={getPeriodLabel()} onViewChange={setPublishedViewType} isDarkMode={isDarkMode} />
        </div>

        <div className="chart-item chart-large">
          <UserDownloadsChart data={userDownloadsPerDay} viewType={downloadsViewType} periodLabel={getPeriodLabel()} onViewChange={setDownloadsViewType} />
        </div>

        <div className="chart-item chart-medium">
          <BooksPerLanguageChart data={booksPerLanguage} isDarkMode={isDarkMode} periodLabel={getPeriodLabel()} />
        </div>

        <div className="chart-item chart-medium">
          <BooksPerLevelChart data={booksPerLevel} isDarkMode={isDarkMode} periodLabel={getPeriodLabel()} />
        </div>
      </div>

      {/* Footer */}
      <footer className="dashboard-footer">
        <p>Copyright © 2026 Booktime. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Home;
